// UserDashboard.js
import React, { useState, useEffect } from "react";
import { Typography, Box, Card, Tabs, Tab, Button } from "@mui/material";
// import ReactMapGL from "react-map-gl"; 
import { useNavigate } from "react-router-dom";
import axios from "axios";

const UserDashboard = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const navigate = useNavigate();

  // Toggle between Attendance and Task views
  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex);
  };

  // ----------------------------
  // Current Date Formatter
  // ----------------------------
  const formatDate = (date) => {
    const options = { day: "numeric", month: "long"};
    return date.toLocaleDateString("en-GB", options);
  };

  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    const now = new Date();
    const msUntilMidnight =
      new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1) - now;

    const timer = setTimeout(() => {
      setCurrentDate(new Date());
    }, msUntilMidnight);

    return () => clearTimeout(timer);
  }, [currentDate]);

  // ----------------------------
  // Attendance Stats (from Backend)
  // ----------------------------
  const [attendanceStats, setAttendanceStats] = useState({
    checkedIn: 0,
    checkedOut: 0,
    absent: 0,
    leave: 0,
    pending: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const fetchAttendanceData = async () => {
      try {
        const [checkedInRes, checkedOutRes, leaveRes, pendingRes, absentRes] =
          await Promise.all([
            axios.get("https://attendanceapi.gensparc.com/count/punch/checkIn", config),
            axios.get("https://attendanceapi.gensparc.com/count/punch/checkOut", config),
            axios.get("https://attendanceapi.gensparc.com/count/leave", config),
            axios.get("https://attendanceapi.gensparc.com/count/pending", config),
            axios.get("https://attendanceapi.gensparc.com/count/absent", config),
          ]);

        console.log("CheckedIn Response:", checkedInRes.data);
        console.log("CheckedOut Response:", checkedOutRes.data);
        console.log("Leave Response:", leaveRes.data);
        console.log("Pending Response:", pendingRes.data);
        console.log("Absent Response:", absentRes.data);

        setAttendanceStats({
          checkedIn: checkedInRes.data.count,
          checkedOut: checkedOutRes.data.count,
          leave: leaveRes.data.count,
          pending: pendingRes.data.count,
          absent: absentRes.data.absentEmployees,
        });
      } catch (error) {
        console.error("Error fetching attendance stats:", error);
      }
    };

    fetchAttendanceData();
  }, []);

  const computedAttendanceStats = [
    { label: "Checked In", value: attendanceStats.checkedIn, color: "green" },
    { label: "Checked Out", value: attendanceStats.checkedOut, color: "red" },
    { label: "Absent", value: attendanceStats.absent, color: "orange" },
    { label: "Leave", value: attendanceStats.leave, color: "blue" },
    { label: "Pending Approval", value: attendanceStats.pending, color: "purple" },
  ];

  // ----------------------------
  // Task Counts (from Backend)
  // ----------------------------
  const [taskCounts, setTaskCounts] = useState({
    Start: 0,
    Pending: 0,
    Completed: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const statuses = ["Start", "Pending", "Completed"];

    Promise.all(
      statuses.map((status) =>
        axios
          .get(`https://attendanceapi.gensparc.com/task/count/${status}`, config)
          .then((response) => ({ status, count: response.data.count }))
          .catch((error) => {
            console.error(`Error fetching count for ${status}:`, error);
            return { status, count: 0 };
          })
      )
    ).then((results) => {
      const counts = results.reduce((acc, { status, count }) => {
        acc[status] = count;
        return acc;
      }, {});
      setTaskCounts(counts);
    });
  }, []);

  const computedTaskStats = [
    { label: "Start", value: taskCounts.Start, color: "gray" },
    { label: "Pending", value: taskCounts.Pending, color: "orange" },
    { label: "Completed", value: taskCounts.Completed, color: "green" },
  ];

  const handleDetailNavigation = (detailType) => {
    if (tabIndex === 0) {
      switch (detailType) {
        case "Checked In":
          navigate(`/CheckedInDetails/${encodeURIComponent(detailType)}`);
          break;
        case "Checked Out":
          navigate(`/checkedout-details/${encodeURIComponent(detailType)}`);
          break;
        case "Absent":
          navigate(`/absent-details/${encodeURIComponent(detailType)}`);
          break;
        case "Leave":
          navigate(`/leave-details/${encodeURIComponent(detailType)}`);
          break;
        case "Pending Approval":
          navigate(`/pending-details/${encodeURIComponent(detailType)}`);
          break;
        default:
          console.error("Unknown attendance type:", detailType);
          break;
      }
    } else {
      navigate(`/task-details/${detailType}`);
    }
  };

  const handleNavigationClick = () => {
    if (tabIndex === 0) {
      navigate("/attendance-page");
    } else {
      navigate("/task-page");
    }
  };

  // ----------------------------
  // Map Viewport State (Example using Mapbox)
  // ----------------------------
  // const [viewport, setViewport] = useState({
  //   latitude: 28.6139,
  //   longitude: 77.209,
  //   zoom: 4,
  //   width: "100%",
  //   height: "100%",
  // });

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="start"
      sx={{ padding: 2, background: "#f5f5f5", minHeight: "100vh", marginTop: "64px" }}
    >
      {/* Current Date Top-Left */}
      <Box sx={{ position: "fixed", top: "74px", left: "105px",right: "16px", zIndex: 1200, backgroundColor: "#0c5273",
            borderRadius: "50px",
            width: "100%",
            maxWidth: "135px",
             height: "36px",
            minHeight: "36px",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)" }}>
        <Typography variant="h6" fontWeight="bold" color="white" sx={{ marginLeft:"0px" ,fontSize: "18px", textAlign: "center",
             paddingTop:"4px"}}>
          {formatDate(currentDate)}
        </Typography>
      </Box>

      {/* Tabs for Attendance and Task */}
      <div style={{ paddingTop: "20px" }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{
            mb: 0,
            backgroundColor: "#f0f0f0",
            borderRadius: "50px",
            width: "100%",
            maxWidth: "210px",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
            height: "36px",
            minHeight: "36px",
            position: "fixed",
            top: "74px",
            right: "16px",
            zIndex: 1200,
            "& .MuiTabs-indicator": {
              backgroundColor: "#0c5273",
              height: "100%",
              borderRadius: "50px",
              zIndex: 1,
            },
          }}
          indicatorColor="secondary"
          textColor="secondary"
          centered
        >
          <Tab
            label="Attendance"
            sx={{
              fontWeight: "bold",
              borderRadius: "50px",
              color: tabIndex === 0 ? "#fff" : "#000",
              zIndex: 2,
              transition: "color 0.3s ease",
              fontSize: "0.875rem",
              padding: "10px 12px",
              minHeight: "36px",
              "&.Mui-selected": { color: "#fff" },
            }}
          />
          <Tab
            label="Task"
            sx={{
              fontWeight: "bold",
              borderRadius: "50px",
              color: tabIndex === 1 ? "#fff" : "#000",
              zIndex: 2,
              transition: "color 0.3s ease",
              fontSize: "0.875rem",
              padding: "6px 12px",
              minHeight: "36px",
              "&.Mui-selected": { color: "#fff" },
            }}
          />
        </Tabs>
      </div>

      {/* Display Counters as Cards */}
      <Box display="flex" flexWrap="wrap" justifyContent="center" sx={{ mt: 1, mb: 1, width: "100%", maxWidth: "1200px" }}>
        {(tabIndex === 0 ? computedAttendanceStats : computedTaskStats).map((stat, index) => (
          <Box key={index} sx={{ width: { xs: "50%", sm: "33.33%", md: "20%" }, padding: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Card
              onClick={() => handleDetailNavigation(stat.label)}
              sx={{
                backgroundColor: stat.color,
                color: "white",
                textAlign: "center",
                padding: 2,
                boxShadow: 4,
                height: "100px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                width: "220px",
                cursor: "pointer",
              }}
            >
              <Typography variant="body1">{stat.label}</Typography>
              <Typography variant="h4">{stat.value}</Typography>
            </Card>
          </Box>
        ))}
      </Box>

      {/* General Navigation Button */}
      <Button variant="contained" color="primary" sx={{ marginBottom: 2 }} onClick={handleNavigationClick}>
        {tabIndex === 0 ? "View Attendance" : "View Task"}
      </Button>

      {/* Map Section */}
      {/* <Box width="100%" maxWidth="1200px" height="300px" sx={{ border: "1px solid #ddd", borderRadius: 2, overflow: "hidden", background: "#eaeaea" , cursor:"pointer" }}>
        <ReactMapGL
          {...viewport}
          mapStyle="mapbox://styles/mapbox/streets-v11"
          mapboxAccessToken="pk.eyJ1IjoicmFqbmVlc2gwMSIsImEiOiJjbThjbzdtOGQwbGdkMm1zNnVsYmJ6N3VlIn0.XrfEqYl7KQ_-WwgYvzeXYQ"  // Replace with your actual Mapbox token
          onMove={(evt) => setViewport(evt.viewState)}
        />
      </Box> */}
    </Box>
  );
};

export default UserDashboard;
